import React, { useEffect, useState, useRef } from "react";
import {
  Cell,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@vds/tables";
import SearchInput from "../../utility/Utils";
import { Checkbox } from "@vds/checkboxes";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { fetchAvailableEditUsers, fetchAvailableAddUsers } from "./utils";
import { useFormikContext } from "formik";
import { Body } from "@vds/typography";
import {
  ResponsiveTableWrapper,
  FlexContainer,
  UserNameCell,
  EmailCell,
} from "../../../styles/sitesModule.styles";
import { Loader } from "@vds/loaders";
import { BANNER_TYPES, USER_MANAGEMENT_ERROR_MESSAGES } from "../../constants/constants";
import ErrorTemplate from "../errorTemplate/ErrorTemplate";
import { TEXT as ERROR_TEMPLATE_TEXT } from "../../constants/errorTemplateConstants";
import { Box } from "@mui/material";

function AddUserTable({ isEditable, setUserInfo, onFetchError }) {
  const { values, setFieldValue } = useFormikContext();
  const { activeSession } = useSelector(
    (state) => state.sessionReducer.activeSession
  );
  const [data, setData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedUsers, setSelectedUsers] = useState(values?.users || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const location = useLocation();
  const mode = location.state?.mode === "edit" ? "edit" : "add";
  const usersInfo = values?.users || [];

  const siteId = values?.siteTagId || [];

  const loadData = async () => {
    setLoading(true);
    setError(false);
    onFetchError?.(false);
    try {
      if (mode === "add") {
        const users = await fetchAvailableAddUsers(activeSession);
        setData(users);
      } else if (mode === "edit") {
        if (!isEditable) {
          const users = await fetchAvailableEditUsers(siteId, activeSession);
          setData(users);
          setSelectedUsers(users);
          setUserInfo(users);
        } else {
          const [allUsers, associatedUsers] = await Promise.all([
            fetchAvailableAddUsers(activeSession),
            fetchAvailableEditUsers(siteId, activeSession),
          ]);
          const associatedIds = associatedUsers.map((user) => user.id);
          const combined = allUsers.map((user) =>
            associatedIds.includes(user.id)
              ? { ...user, selected: true }
              : { ...user, selected: false }
          );
          setData(combined);
          const initiallySelected = combined.filter((user) => user.selected);
          setSelectedUsers(initiallySelected);
          setUserInfo(initiallySelected);
        }
      }
    } catch {
      setError(true);
      onFetchError?.(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [mode, isEditable]);

  useEffect(() => {
    if (JSON.stringify(values?.users) !== JSON.stringify(selectedUsers)) {
      setFieldValue("users", selectedUsers);
    }
  }, [selectedUsers, setFieldValue]);

  const handleSearchInput = (e) => {
    setSearchValue(e.target.value);
  };

  const handleCheckboxChange = (user, checked) => {
    setSelectedUsers((prevSelectedUsers) => {
      if (checked) {
        return [...prevSelectedUsers, user];
      } else {
        return prevSelectedUsers.filter(
          (selectedUser) => selectedUser.id !== user.id
        );
      }
    });
  };

  const filteredData = data?.filter((user) => {
    const fullName = `${user.firstname} ${user.lastname}`.toLowerCase();
    const email = user.email.toLowerCase();
    const search = searchValue.toLowerCase();
    return fullName.includes(search) || email.includes(search);
  });
  useEffect(() => {
    if (
      data?.length > 0 &&
      values?.siteTagId &&
      !(mode === "edit" && isEditable)
    ) {
      const items =
        mode === "edit"
          ? data
          : data.filter((user) => user.tagids?.includes(values.siteTagId));
      setUserInfo(items);
      setSelectedUsers(items);
    }
  }, [data, values?.siteTagId, mode, isEditable]);
  if (loading) {
    return (
        <div className="loader-absolute-container">
          <Loader active={true} />
        </div>
    );
  }

  if (error) {
    return (
      <Box sx={{ height: "300px" }}>
      <ErrorTemplate
        templateType="info"
        title={USER_MANAGEMENT_ERROR_MESSAGES.FETCHUSERS_FAILED_TITLE}
        description={(isEditable || mode === "add") ? USER_MANAGEMENT_ERROR_MESSAGES.FETCH_USER_GROUPS_OPTIONAL_FAILED : USER_MANAGEMENT_ERROR_MESSAGES.FETCH_USERGROUPS_FAILED }
        onRefresh={loadData}
        testId="add-user-table-error"
      />
      </Box>
    );
  }

  const displayData = mode === "add" || (mode === "edit" && isEditable)
    ? filteredData
    : selectedUsers;

  if (mode === "add" && !error && (!displayData || displayData.length === 0)) {
    return (
      <Box sx={{ height: "300px" }}>
        <ErrorTemplate
          title={USER_MANAGEMENT_ERROR_MESSAGES.EMPTY_USERS_TITLE}
          description={USER_MANAGEMENT_ERROR_MESSAGES.EMPTY_USERS}
          onRefresh={loadData}
          templateType="info"
          testId="add-user-table-empty"
        />
      </Box>
    );
  }

  return (
    <div>
      {(mode === "add" || (mode === "edit" && isEditable)) && (
        <SearchInput
          onChange={handleSearchInput}
          addUsers={true}
          placeholder="Search users"
        />
      )}
      <ResponsiveTableWrapper>
        <Table striped={false} bottomLine="secondary" padding="compact">
          <TableHead bottomLine="primary">
            <TableHeader>
              <Body size="large" bold>
                User name
              </Body>
            </TableHeader>
            <TableHeader>
              <Body size="large" bold>
                Email
              </Body>
            </TableHeader>
          </TableHead>
          <TableBody>
            {displayData?.map((user) => (
              <TableRow key={user.id}>
                <UserNameCell>
                  <FlexContainer>
                    {(mode === "add" || (mode === "edit" && isEditable)) && (
                      <Checkbox
                        name="default"
                        width="auto"
                        error={false}
                        selected={selectedUsers.some(
                          (selectedUser) => selectedUser.id === user.id
                        )}
                        onChange={(e) =>
                          handleCheckboxChange(user, e.target.checked)
                        }
                      />
                    )}
                    {user?.firstname} {user?.lastname}
                  </FlexContainer>
                </UserNameCell>
                <EmailCell>{user.email}</EmailCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTableWrapper>
    </div>
  );
}

export default AddUserTable;
